import React, { useCallback, useMemo, useState } from 'react';
import { Button, Modal, PageHeader, Table, message as antdMessage } from 'antd';
import moment from 'moment';

import Content from '../../component/Content';
import useGetPathwayScormApprovalsQuery from './queries/useGetPathwayScormApprovalsQuery';
import useApprovePathwayScormMutation from './queries/useApprovePathwayScormMutation';
import useRejectPathwayScormMutation from './queries/useRejectPathwayScormMutation';

import s from './index.module.scss';

const { confirm } = Modal;

const getRequest = record => record.request || {};

const PathwayScormApprovals = () => {
  const { data = [], isLoading } = useGetPathwayScormApprovalsQuery();
  const approveMutation = useApprovePathwayScormMutation();
  const rejectMutation = useRejectPathwayScormMutation();
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const announce = useCallback(text => {
    setStatusMessage(text);
  }, []);

  const onApprove = useCallback(
    record => {
      const title = record.title || 'this pathway';
      confirm({
        title: `Approve SCORM changes for "${title}"?`,
        content:
          'This will publish a new pathway version using the existing versioning flow. Assigned users will be migrated according to the original request.',
        okText: 'Approve',
        onOk: () =>
          approveMutation.mutateAsync(record.id).then(() => {
            announce(`Approved SCORM changes for ${title}`);
          }),
      });
    },
    [announce, approveMutation]
  );

  const onRejectSubmit = () => {
    const reason = rejectionReason.trim();
    if (!reason) {
      antdMessage.error('A rejection reason is required');
      return;
    }
    const title = rejectTarget.title || 'this pathway';
    rejectMutation
      .mutateAsync({ id: rejectTarget.id, rejectionReason: reason })
      .then(() => {
        announce(`Rejected SCORM changes for ${title}`);
        setRejectTarget(null);
        setRejectionReason('');
      });
  };

  const columns = useMemo(
    () => [
      {
        title: 'Hub',
        dataIndex: 'hubName',
        key: 'hubName',
        render: (hubName, record) => (
          <span>
            {hubName}
            {record.hubIdentifier ? ` (${record.hubIdentifier})` : ''}
          </span>
        ),
      },
      {
        title: 'Pathway',
        dataIndex: 'title',
        key: 'title',
        render: (title, record) => (
          <span>
            {title}
            {record.version ? ` · v${record.version}` : ''}
          </span>
        ),
      },
      {
        title: 'Type',
        key: 'requestType',
        render: (_, record) => {
          const type = getRequest(record).requestType;
          return type === 'publish' ? 'First publish' : 'Published edit';
        },
      },
      {
        title: 'Requested by',
        key: 'requestedBy',
        render: (_, record) => getRequest(record).requestedByName || '—',
      },
      {
        title: 'Requested at',
        key: 'requestedAt',
        render: (_, record) => {
          const { requestedAt } = getRequest(record);
          return requestedAt ? moment(requestedAt).format('L LT') : '—';
        },
      },
      {
        title: 'Changes',
        key: 'changelog',
        render: (_, record) => {
          const changelog = getRequest(record).payload?.changelog || [];
          if (!changelog.length) return '—';
          return (
            <ul className={s.changelog}>
              {changelog.map(change => (
                <li key={change.key || change.value}>{change.value}</li>
              ))}
            </ul>
          );
        },
      },
      {
        title: 'SCORM URLs',
        key: 'scormUrls',
        render: (_, record) => {
          const urls = getRequest(record).scormUrls || [];
          if (!urls.length) return '—';
          return (
            <ul className={s.urls}>
              {urls.map(url => (
                <li key={url}>
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    {url}
                  </a>
                </li>
              ))}
            </ul>
          );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => {
          const title = record.title || 'pathway';
          return (
            <div className={s.actions}>
              <Button
                type="primary"
                size="small"
                onClick={() => onApprove(record)}
                aria-label={`Approve SCORM changes for ${title}`}
              >
                Approve
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setRejectTarget(record);
                  setRejectionReason('');
                }}
                aria-label={`Reject SCORM changes for ${title}`}
              >
                Reject
              </Button>
            </div>
          );
        },
      },
    ],
    [onApprove]
  );

  return (
    <Content className={s.page}>
      <PageHeader title="Pathway SCORM Approvals" />
      <div role="status" aria-live="polite" className={s.visuallyHidden}>
        {statusMessage}
      </div>
      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={data}
        pagination={{ pageSize: 20 }}
      />
      <Modal
        title={`Reject SCORM changes${
          rejectTarget ? ` for "${rejectTarget.title}"` : ''
        }`}
        visible={!!rejectTarget}
        onCancel={() => {
          setRejectTarget(null);
          setRejectionReason('');
        }}
        onOk={onRejectSubmit}
        okText="Reject"
        confirmLoading={rejectMutation.isLoading}
        okButtonProps={{ disabled: !rejectionReason.trim() }}
      >
        <label htmlFor="scorm-rejection-reason" className={s.rejectField}>
          Rejection reason
          <textarea
            id="scorm-rejection-reason"
            className="ant-input"
            value={rejectionReason}
            onChange={e => setRejectionReason(e.target.value)}
            rows={4}
            aria-required="true"
          />
        </label>
      </Modal>
    </Content>
  );
};

export default PathwayScormApprovals;
